/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from 'react';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import {
  SELECTABLE_PALETTE,
  calendarColor,
} from '@features/agenda/utils/calendar-display';

/** Sentinel id for the create editor, which has no calendar behind it yet. */
export const NEW_CALENDAR = 'new';

export interface CalendarDraft {
  editingId: string | null;
  name: string;
  color: string;
  startCreate: () => void;
  startEdit: (calendar: Calendar) => void;
  setName: (name: string) => void;
  setColor: (color: string) => void;
  cancel: () => void;
  /** Clears the draft once a save round-trip finishes. */
  finish: () => void;
}

/**
 * Holds the one calendar being written at a time.
 *
 * There is a single draft rather than one per row because the editor takes
 * the row's place: two open editors would mean two rows gone from the list.
 *
 * @param open - Whether the settings are visible; closing discards the draft.
 * @returns The draft state and the transitions that move it.
 */
export function useCalendarDraft(open: boolean): CalendarDraft {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(SELECTABLE_PALETTE[0]);

  useEffect(() => {
    if (!open) setEditingId(null);
  }, [open]);

  const startCreate = useCallback(() => {
    setEditingId(NEW_CALENDAR);
    setName('');
    setColor(SELECTABLE_PALETTE[0]);
  }, []);

  const startEdit = useCallback((calendar: Calendar) => {
    setEditingId(calendar.calendar_id ?? null);
    setName(calendar.name ?? '');
    setColor(calendarColor(calendar));
  }, []);

  const cancel = useCallback(() => setEditingId(null), []);

  return {
    editingId,
    name,
    color,
    startCreate,
    startEdit,
    setName,
    setColor,
    cancel,
    finish: cancel,
  };
}
